import type { CSSProperties } from "react";

import type { DocumentViewportValue } from "@/features/document-viewport/types";
import { pageWidthToScreenLength } from "@/viewport-tools/measurement/overlay/interactions/transform-interactions";

// Card metrics as em ratios, mirroring takeoff_pdf_export.py's LABEL_CARD_* points over its
// 8.5pt base font. A locked card is laid out in PDF points on both sides, so the two must agree
// or the same textBoxWidth wraps differently in the overlay than it does in the export.
const LABEL_CARD_PADDING_X_EM = 6.0 / 8.5;
const LABEL_CARD_PADDING_Y_EM = 4.0 / 8.5;
const LABEL_CARD_CORNER_RADIUS_EM = 4.0 / 8.5;

// Helvetica Bold matches ReportLab LABEL_FONT_NAME so locked-width wrap decisions
// agree between the viewport and PDF export.
export const MEASUREMENT_LABEL_CARD_CLASS_NAME =
  "border bg-background/90 text-center font-bold leading-tight shadow-sm";

export function getMeasurementLabelCardStyle({
  borderColor,
  fontSize,
  textBoxWidthCssPx,
}: {
  borderColor: string;
  fontSize: number;
  textBoxWidthCssPx?: number;
}): CSSProperties {
  const hasFixedWidth = textBoxWidthCssPx !== undefined && textBoxWidthCssPx > 0;
  return {
    fontFamily: 'Helvetica, Arial, "Helvetica Neue", sans-serif',
    fontSize,
    padding: `${LABEL_CARD_PADDING_Y_EM}em ${LABEL_CARD_PADDING_X_EM}em`,
    borderRadius: `${LABEL_CARD_CORNER_RADIUS_EM}em`,
    lineHeight: 1.05,
    borderColor,
    color: "var(--foreground)",
    ...(hasFixedWidth
      ? {
          width: textBoxWidthCssPx,
          whiteSpace: "normal",
          overflowWrap: "anywhere",
          wordBreak: "break-word",
        }
      : {
          whiteSpace: "nowrap",
        }),
  };
}

export function MeasurementLabelCardContent({
  label,
  annotationLine,
  valueLine,
}: {
  label: string;
  annotationLine?: string;
  valueLine?: string;
}) {
  return (
    <>
      <div>{label}</div>
      {annotationLine ? (
        <div
          className="whitespace-pre-line font-medium opacity-80"
          style={{ fontSize: "0.85em" }}
        >
          {annotationLine}
        </div>
      ) : null}
      {valueLine ? <div>{valueLine}</div> : null}
    </>
  );
}

/**
 * Screen pixels per PDF point for the rendered page, or null when the page size is unknown.
 */
export function getScreenPxPerPagePoint(viewport: DocumentViewportValue): number | null {
  const pagePointWidth = viewport.activePageMetrics?.width ?? 0;
  if (pagePointWidth <= 0) {
    return null;
  }
  const screenWidth = pageWidthToScreenLength(1, viewport.transforms.pageToScreenPoint);
  return screenWidth > 0 ? screenWidth / pagePointWidth : null;
}

export function getMeasurementLabelScale(
  baseFontSize: number,
  screenPxPerPagePoint: number | null,
  viewportScale: number,
  textLocked: boolean
): { baseFontSize: number; scale: number } {
  if (textLocked) {
    const scale = screenPxPerPagePoint ?? (viewportScale > 0 ? viewportScale : 1);
    return { baseFontSize, scale: scale > 0 ? scale : 1 };
  }
  return { baseFontSize, scale: 1 };
}
